import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ban, Copy, Download, FileUp, Link2, Lock, PenLine, Printer, Undo2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReasonDialog } from "@/components/ReasonDialog";
import { ShareButton } from "@/components/ShareDialog";
import { api, errMsg, fmtDateTime, openPdf } from "@/lib/api";

const Card = ({ title, children, testId }) => (
  <section className="rounded-xl border border-[#D4E6EC] bg-white p-5 sm:p-7 space-y-4" data-testid={testId}>
    <h3 className="font-serif-it text-2xl font-semibold">{title}</h3>
    {children}
  </section>
);

export async function uploadFile(cid, file, category, note = "") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  fd.append("note", note);
  const r = await api.post(`/contracts/${cid}/files`, fd);
  return r.data;
}

export function PaperUpload({ cid, scope = "base", category, onDone, testId }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const f = await uploadFile(cid, file, category, "Scansione firmata");
      await api.post(`/contracts/${cid}/paper-signed?scope=${scope}`, { file_id: f.id });
      toast.success("Firma cartacea registrata");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer text-sm px-4 py-2 rounded-full border border-[#D4E6EC] hover:border-[#0B6E8F] bg-white transition-colors">
      <FileUp size={15} /> {busy ? "Caricamento…" : "Carica scansione firmata"}
      <input type="file" accept="application/pdf,image/*" className="hidden" data-testid={testId} onChange={pick} disabled={busy} />
    </label>
  );
}

function RemoteLink({ c, reload }) {
  const url = c.remote_active && c.remote_token ? `${window.location.origin}/firma/${c.remote_token}` : "";
  const gen = async () => {
    try { await api.post(`/contracts/${c.id}/remote-link`); toast.success("Link generato"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async () => {
    await api.delete(`/contracts/${c.id}/remote-link`);
    toast.success("Link revocato");
    reload();
  };
  if (!url) return <Button variant="outline" className="rounded-full" onClick={gen} data-testid="remote-link-generate"><Link2 size={15} className="mr-2" /> Genera link firma remota</Button>;
  const body = `🌟 Gentile *${c.data?.cliente_nome || "Cliente"}*,\n\n✍️ come concordato, le inviamo il link personale per leggere e firmare comodamente dal telefono 📱 il *contratto n. ${c.number}*.\n👉 Le basta seguire i passaggi indicati, foglio per foglio.\n🔒 Il link è riservato e valido 14 giorni.\n\n💬 Per qualsiasi dubbio siamo a sua disposizione.\n\nUn cordiale saluto 🌊`;
  return (
    <div className="space-y-2" data-testid="remote-link-box">
      <div className="text-xs text-[#47606B]">Link attivo fino al {fmtDateTime(c.remote_expires)}{c.remote_opened_at ? ` • aperto dal cliente il ${fmtDateTime(c.remote_opened_at)}` : ""}</div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input readOnly value={url} data-testid="remote-link-url" className="flex-1 font-mono-it text-xs rounded-md border border-[#D4E6EC] bg-[#EAF5F7] px-3 py-2" />
        <Button variant="outline" size="sm" data-testid="remote-link-copy" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copiato"); }}><Copy size={14} className="mr-1" /> Copia</Button>
        <ShareButton size="sm" testId="remote-link-whatsapp" label="WhatsApp" link={url} phone={c.data?.cliente_tel} body={body} />
        <Button variant="ghost" size="sm" data-testid="remote-link-revoke" onClick={revoke}><XCircle size={14} className="mr-1" /> Revoca</Button>
      </div>
    </div>
  );
}

export function SignPanel({ c, reload }) {
  const nav = useNavigate();
  const [reopen, setReopen] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [missing, setMissing] = useState("");
  const base = (c.pending || []).filter((i) => i.kind === "base");
  const anyPending = (c.pending || []).length > 0;

  const send = async () => {
    setMissing("");
    try {
      await api.post(`/contracts/${c.id}/send-to-sign`);
      toast.success("Contenuto bloccato: contratto pronto per la firma");
      reload();
    } catch (e) {
      setMissing(errMsg(e));
    }
  };

  return (
    <div className="space-y-6 fade-up">
      {c.status === "bozza" && (
        <Card title="1. Blocca il contenuto e avvia la firma" testId="sign-panel-draft">
          <p className="text-sm text-[#47606B]">Verificati i dati, il contenuto viene bloccato e ne viene calcolata l'impronta digitale (SHA-256). Ogni firma resta legata a quel contenuto: per modificare occorre riportare in bozza, annullando le firme raccolte.</p>
          {missing && <div className="text-sm bg-[#FEE2E2] text-[#991B1B] rounded-md p-3" data-testid="send-to-sign-error">{missing}</div>}
          <div className="flex flex-wrap gap-2">
            <Button className="rounded-full" onClick={send} data-testid="send-to-sign-button"><Lock size={15} className="mr-2" /> Blocca e invia in firma</Button>
            <Button variant="outline" className="rounded-full" onClick={() => openPdf(`/contracts/${c.id}/pdf`)} data-testid="preview-pdf-button"><Printer size={15} className="mr-2" /> Anteprima PDF</Button>
          </div>
        </Card>
      )}

      {c.status === "in_firma" && (
        <Card title="Contratto in firma" testId="sign-panel-in-progress">
          <div className="text-xs font-mono-it text-[#47606B] break-all" data-testid="content-hash">Impronta contenuto SHA-256: {c.content_hash}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {base.map((i) => (
              <div key={i.page_key} className={`rounded-lg border p-3 text-sm ${i.missing.length ? "border-[#D4E6EC]" : "border-[#BBF7D0] bg-[#F0FDF4]"}`} data-testid={`sheet-status-${i.page_key}`}>
                <div className="font-medium">{i.label}</div><div className="text-xs text-[#47606B]">{i.signed.length}/{i.roles.length} firme</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button className="rounded-full" onClick={() => nav(`/contratti/${c.id}/firma`)} data-testid="sign-in-person-button"><PenLine size={15} className="mr-2" /> Firma su schermo (in presenza)</Button>
            <Button variant="ghost" className="rounded-full" onClick={() => setReopen(true)} data-testid="reopen-button"><Undo2 size={15} className="mr-2" /> Riporta in bozza</Button>
          </div>
        </Card>
      )}

      {anyPending && (
        <Card title="Firma a distanza" testId="sign-panel-remote">
          <p className="text-sm text-[#47606B]">Il cliente riceve un link personale (valido 14 giorni) e firma dal proprio telefono, foglio per foglio. Apertura e firme vengono registrate con data, ora, IP e dispositivo.</p>
          <RemoteLink c={c} reload={reload} />
        </Card>
      )}

      {c.status === "in_firma" && (
        <Card title="Firma su carta" testId="sign-panel-paper">
          <p className="text-sm text-[#47606B]">Stampa il contratto compilato, fai firmare a mano ogni foglio (e la firma specifica a pag. 4), poi carica la scansione: il contratto risulterà firmato in modalità cartacea con impronta del file.</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => openPdf(`/contracts/${c.id}/pdf`)} data-testid="print-pdf-button"><Printer size={15} className="mr-2" /> Stampa contratto</Button>
            <PaperUpload cid={c.id} category="Contratto firmato cartaceo" onDone={reload} testId="paper-upload-input" />
          </div>
        </Card>
      )}

      {c.status === "firmato" && (
        <Card title="Contratto firmato" testId="sign-panel-signed">
          <div className="text-sm">Firmato il {fmtDateTime(c.signed_at)} • modalità <b>{c.signing_method === "cartacea" ? "cartacea (scansione)" : "elettronica su schermo"}</b></div>
          <div className="text-xs font-mono-it text-[#47606B] break-all" data-testid="final-pdf-hash">SHA-256 documento definitivo: {c.final_pdf_sha256}</div>
          <Button className="rounded-full" onClick={() => openPdf(`/contracts/${c.id}/final-pdf`)} data-testid="final-pdf-button"><Download size={15} className="mr-2" /> Scarica documento firmato</Button>
        </Card>
      )}

      {c.status !== "annullato" ? (
        <button className="text-sm text-[#991B1B] inline-flex items-center gap-2 hover:underline" onClick={() => setCancel(true)} data-testid="cancel-contract-button"><Ban size={14} /> Annulla contratto</button>
      ) : (
        <div className="text-sm text-[#991B1B]" data-testid="cancelled-info">Contratto annullato: {c.cancel_reason}</div>
      )}

      <ReasonDialog open={reopen} onOpenChange={setReopen} testId="reopen" title="Riportare in bozza?" confirmLabel="Riporta in bozza"
        description="Tutte le firme già raccolte sul contratto verranno annullate (l'operazione resta nel registro)."
        onConfirm={async (reason) => { try { await api.post(`/contracts/${c.id}/reopen`, { reason }); toast.success("Contratto riportato in bozza"); reload(); } catch (e) { toast.error(errMsg(e)); } }} />
      <ReasonDialog open={cancel} onOpenChange={setCancel} testId="cancel" title="Annullare il contratto?" confirmLabel="Annulla contratto"
        description="Il contratto resterà in archivio con stato «Annullato»."
        onConfirm={async (reason) => { try { await api.post(`/contracts/${c.id}/cancel`, { reason }); toast.success("Contratto annullato"); reload(); } catch (e) { toast.error(errMsg(e)); } }} />
    </div>
  );
}
